import React, { useState, useEffect } from "react";
import SEO from "../component/SEO";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import emptyCartImage from "../images/emptyy.png";
import { increaseQty, decreaseQty, deleteCartItem, clearCart } from "../redux/productSlice";
import { businessInfo } from "../config/business";

const Cart = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const productCartItem = useSelector((state) => state.product.cartItem);
  const user = useSelector((state) => state.user);

  // Pickup / Delivery
  const [deliveryType, setDeliveryType] = useState("pickup");

  // ----- UPDATED: enforce minimum 3 days prior -----
  const getMinAllowedDate = () => {
    const minDate = new Date();
    minDate.setDate(minDate.getDate() + 3); // minimum = today + 3 days
    return minDate.toISOString().split("T")[0];
  };

  const minAllowedDate = getMinAllowedDate();

  const [pickupDate, setPickupDate] = useState(() => {
    // default to min allowed date
    return getMinAllowedDate();
  });

  // ----- UPDATED: timing constraints 09:00 - 23:00 -----
  const MIN_TIME = "09:00";
  const MAX_TIME = "23:00";

  const [pickupTime, setPickupTime] = useState("09:00");

  // Delivery Address & Distance
  const [deliveryLocation, setDeliveryLocation] = useState(null);
  const [distanceKm, setDistanceKm] = useState(0);

  const [recipientName, setRecipientName] = useState("");
  const [recipientPhone, setRecipientPhone] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [houseNumber, setHouseNumber] = useState("");
  const [postalCode, setPostalCode] = useState("");

  // OTP Flow
  const [orderId, setOrderId] = useState(null);
  const [enteredOtp, setEnteredOtp] = useState("");
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [orderConfirmed, setOrderConfirmed] = useState(false);

  // Bakery coordinates
  const bakeryLat = businessInfo.coordinates.lat;
  const bakeryLng = businessInfo.coordinates.lng;

  // Cart calculations
  const subtotal = productCartItem.reduce(
    (acc, item) => acc + Number(item.finalPrice || item.price) * Number(item.qty),
    0
  );
  const totalItems = productCartItem.reduce((acc, item) => acc + Number(item.qty), 0);
  const deliveryCharge = deliveryType === "delivery" ? Number(distanceKm || 0) * 1 : 0;
  const totalBill = subtotal + deliveryCharge;

  // Redirect if not logged in
  useEffect(() => {
    if (!user.token) {
      toast.error("Please login to access your cart.");
      navigate("/login");
    }
  }, [user.token, navigate]);

  // Ensure pickupDate never falls before minAllowedDate (covers phones/browsers that allow manual earlier selection)
  useEffect(() => {
    if (!pickupDate) {
      setPickupDate(minAllowedDate);
      return;
    }
    if (pickupDate < minAllowedDate) {
      setPickupDate(minAllowedDate);
      toast.error(`Pickup/Delivery date must be at least ${minAllowedDate}`);
    }
    // recompute minAllowedDate periodically isn't necessary here — it's computed on mount.
    // If you want minAllowedDate to update at midnight while user has page open,
    // you'd add a timer to recalc — omitted intentionally.
  }, [pickupDate, minAllowedDate]);

  // Google Maps logic
  useEffect(() => {
    if (deliveryType === "delivery") {
      const loadGoogleMaps = () => {
        if (!window.google) {
          const script = document.createElement("script");
          script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`;
          script.async = true;
          script.defer = true;
          script.onload = () => initMap();
          document.body.appendChild(script);
        } else {
          initMap();
        }
      };
      loadGoogleMaps();
    }
  }, [deliveryType]);

  const initMap = () => {
    const map = new window.google.maps.Map(document.getElementById("map"), {
      center: deliveryLocation
        ? { lat: deliveryLocation.lat, lng: deliveryLocation.lng }
        : { lat: bakeryLat, lng: bakeryLng },
      zoom: 13,
    });

    // Bakery marker
    new window.google.maps.Marker({
      position: { lat: bakeryLat, lng: bakeryLng },
      map,
      title: "Primary Bakery",
      icon: "http://maps.google.com/mapfiles/ms/icons/red-dot.png",
    });

    // User marker
    let userMarker;
    if (deliveryLocation) {
      userMarker = new window.google.maps.Marker({
        position: { lat: deliveryLocation.lat, lng: deliveryLocation.lng },
        map,
        title: "Your Delivery Location",
        draggable: true,
      });
    }

    // Click on map
    map.addListener("click", (e) => {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();

      if (userMarker) userMarker.setPosition(e.latLng);
      else {
        userMarker = new window.google.maps.Marker({
          position: e.latLng,
          map,
          title: "Your Delivery Location",
          draggable: true,
        });
      }

      setDeliveryLocation({ lat, lng });
      calculateDistance(lat, lng);

      userMarker.addListener("dragend", (ev) => {
        const latD = ev.latLng.lat();
        const lngD = ev.latLng.lng();
        setDeliveryLocation({ lat: latD, lng: lngD });
        calculateDistance(latD, lngD);
      });
    });
  };

  const calculateDistance = (lat, lng) => {
    const R = 6371;
    const dLat = ((lat - bakeryLat) * Math.PI) / 180;
    const dLng = ((lng - bakeryLng) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos((bakeryLat * Math.PI) / 180) *
        Math.cos((lat * Math.PI) / 180) *
        Math.sin(dLng / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;
    setDistanceKm(distance.toFixed(2));
  };

  // ---------- Time helpers ----------
  const isTimeValid = (time) => {
    if (!time) return false;
    return time >= MIN_TIME && time <= MAX_TIME;
  };

  const clampTimeToRange = (time) => {
    if (!time) return MIN_TIME;
    if (time < MIN_TIME) return MIN_TIME;
    if (time > MAX_TIME) return MAX_TIME;
    return time;
  };

  // Send OTP
  const handleSendOtp = async () => {
    // Validate date server-side / client-side: ensure pickupDate is at least minAllowedDate
    if (!pickupDate || pickupDate < minAllowedDate) {
      toast.error(`Pickup/Delivery date must be at least ${minAllowedDate}`);
      setPickupDate(minAllowedDate);
      return;
    }

    // Validate time is selected and within working hours
    if (!pickupTime || !isTimeValid(pickupTime)) {
      const corrected = clampTimeToRange(pickupTime);
      setPickupTime(corrected);
      toast.error("Our timings are only between 9 AM and 11 PM.");
      return;
    }

    if (deliveryType === "delivery" && !deliveryLocation && !addressLine1)
      return toast.error("Please select delivery location or enter address manually.");
    if (deliveryType === "delivery" && (!recipientName || !recipientPhone || !addressLine1))
      return toast.error("Please fill recipient name, phone, and address.");

    const orderData = {
      items: productCartItem.map((item) => ({
        productId: item._id,
        name: item.name,
        qty: item.qty,
        price: item.finalPrice || item.price,
        selectedOptions: item.selectedOptions || {},
        specialInstructions: item.specialInstructions || "",
      })),
      deliveryType,
      pickupDate,
      pickupTime,
      deliveryAddress:
        deliveryType === "delivery"
          ? {
              recipientName,
              recipientPhone,
              addressLine1,
              houseNumber,
              postalCode,
              location: deliveryLocation,
            }
          : null,
      subtotal,
      deliveryCharge,
      total: totalBill,
    };

    try {
      const res = await fetch(`${process.env.REACT_APP_SERVER_DOMIN}/order/create`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${user.token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(orderData),
      });

      const data = await res.json();

      if (!res.ok) {
        toast.error(data.message || "Failed to create order");
      } else {
        setOrderId(data.orderId);
        setOrderConfirmed(true);
        toast.success("✅ Order confirmed successfully!");
        dispatch(clearCart());
      }
    } catch (err) {
      console.error("❌ Order creation error:", err);
      toast.error("Server error while creating order");
    }
  };

  const handleVerifyOtp = async () => {
    if (!orderId) return toast.error("No order found");
    setOrderConfirmed(true);
    setShowOtpModal(false);
    try {
      const res = await fetch(`${process.env.REACT_APP_SERVER_DOMIN}/order/verify-otp`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${user.token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ orderId, otp: enteredOtp }),
      });

      const data = await res.json();
      if (!res.ok) toast.error(data.message || "Invalid OTP");
      else {
        setOrderConfirmed(true);
        setShowOtpModal(false);

        toast.success("✅ Order confirmed successfully!");
      }
    } catch (err) {
      console.error("❌ OTP verification error:", err);
      toast.error("Server error while verifying OTP");
    }
  };

  const renderCartItem = (item, index) => (
    <div key={`${item._id}-${index}`} className="bg-white rounded-lg shadow p-4 flex flex-col gap-2">
      <div className="flex items-center gap-4">
        <img src={item.image} alt={item.name} className="h-24 w-24 object-contain rounded" />
        <div className="flex-1">
          <h3 className="font-semibold text-lg capitalize">{item.name}</h3>
          <p className="text-gray-600">{item.category}</p>
          <p className="font-bold text-gray-700">
            CAD {item.finalPrice || item.price} x {item.qty}
          </p>
        </div>
      </div>

      {item.selectedOptions && Object.keys(item.selectedOptions).length > 0 && (
        <div className="ml-2 mt-2 text-gray-700">
          {Object.entries(item.selectedOptions).map(([title, choice]) => (
            <p key={title} className="text-sm">
              <span className="font-semibold">{title}:</span>{" "}
              {Array.isArray(choice) ? choice.map((c) => c.name).join(", ") : choice.name}{" "}
              {Array.isArray(choice)
                ? choice.filter((c) => c.price > 0).map((c) => `(+CAD ${c.price})`).join(", ")
                : choice.price > 0
                ? `(+CAD ${choice.price})`
                : ""}
            </p>
          ))}
        </div>
      )}

      {item.specialInstructions && (
        <p className="ml-2 mt-2 text-gray-500 italic text-sm">Note: {item.specialInstructions}</p>
      )}

      <div className="flex items-center gap-2 mt-2">
        <button onClick={() => dispatch(decreaseQty(item._id))} className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300">-</button>
        <span className="px-2">{item.qty}</span>
        <button onClick={() => dispatch(increaseQty(item._id))} className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300">+</button>
        <button onClick={() => dispatch(deleteCartItem(item._id))} className="ml-auto text-red-500 hover:text-red-700 font-semibold">Remove</button>
      </div>
    </div>
  );

  if (productCartItem.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-10">
        <img src={emptyCartImage} alt="Empty cart" className="w-60 md:w-80 object-contain" />
        <p className="mt-4 text-gray-600 text-lg">Your cart is empty! 🛒</p>
        <button onClick={() => navigate("/menu")} className="mt-4 px-6 py-2 bg-pink-600 text-white rounded-full hover:bg-pink-700 transition">Go Shopping</button>
      </div>
    );
  }

  // handle date input change robustly (some phones allow manual past selection)
  const handlePickupDateChange = (e) => {
    const selected = e.target.value;
    if (!selected) {
      setPickupDate(minAllowedDate);
      return;
    }
    if (selected < minAllowedDate) {
      setPickupDate(minAllowedDate);
      toast.error(`Pickup/Delivery date must be at least ${minAllowedDate}`);
    } else {
      setPickupDate(selected);
    }
  };

  // handle time input change robustly (some phones allow manual out-of-range time)
  const handlePickupTimeChange = (e) => {
    const selected = e.target.value;
    if (!selected) {
      setPickupTime(MIN_TIME);
      toast.error("Our timings are only between 9 AM and 11 PM.");
      return;
    }
    if (!isTimeValid(selected)) {
      const corrected = clampTimeToRange(selected);
      setPickupTime(corrected);
      toast.error("Our timings are only between 9 AM and 11 PM.");
    } else {
      setPickupTime(selected);
    }
  };

  return (
    <div className="p-4 md:p-8">
      <SEO title="Your Cart - K Bakery" description="Review your cart and proceed to checkout." />
      <h1 className="text-2xl md:text-3xl font-bold text-pink-700 mb-6">Your Bakery Cart 🍰</h1>
      <div className="flex flex-col lg:flex-row gap-6">
        <div className="flex-1 space-y-4">{productCartItem.map(renderCartItem)}</div>

        <div className="lg:w-1/3 w-full">
          <div className="bg-white rounded-2xl shadow-lg p-6 border">
            <h2 className="text-xl font-bold mb-4 text-center text-blue-600">Order Summary</h2>
            <div className="flex justify-between py-2 text-gray-700 border-b">
              <span>Total Items:</span>
              <span className="font-semibold">{totalItems}</span>
            </div>
            <div className="flex justify-between py-2 text-gray-700 border-b">
              <span>Subtotal:</span>
              <span className="font-semibold">CAD {subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between py-2 text-gray-700 border-b">
              <span>Delivery Charge:</span>
              <span className="font-semibold text-green-600">CAD {deliveryCharge.toFixed(2)}</span>
            </div>
            <div className="flex justify-between py-3 text-lg font-bold text-gray-900 border-b">
              <span>Total:</span>
              <span>CAD {totalBill.toFixed(2)}</span>
            </div>

            <div className="mt-4 flex items-center gap-4">
              {["pickup", "delivery"].map((type) => (
                <label key={type} className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" checked={deliveryType === type} onChange={() => setDeliveryType(type)} />
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                  {type === "delivery" && " (+1 CAD Per km)"}
                </label>
              ))}
            </div>

            {/* Pickup / Delivery Date & Time */}
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block font-semibold mb-1">Pickup / Delivery Date</label>
                <input
                  type="date"
                  min={minAllowedDate} // UPDATED: enforce minimum 3 days ahead
                  className="border w-full p-2 rounded focus:ring-2 focus:ring-pink-400 focus:outline-none"
                  value={pickupDate}
                  onChange={handlePickupDateChange}
                  onBlur={() => {
                    // second-line safety: if user somehow entered an invalid date manually
                    if (!pickupDate || pickupDate < minAllowedDate) setPickupDate(minAllowedDate);
                  }}
                />
              </div>
              <div>
                <label className="block font-semibold mb-1">Time</label>
                <input
                  type="time"
                  min={MIN_TIME}
                  max={MAX_TIME}
                  className="border w-full p-2 rounded focus:ring-2 focus:ring-pink-400 focus:outline-none"
                  value={pickupTime}
                  onChange={handlePickupTimeChange}
                  onBlur={() => {
                    if (!pickupTime || !isTimeValid(pickupTime)) {
                      const corrected = clampTimeToRange(pickupTime);
                      setPickupTime(corrected);
                      toast.error("Our timings are only between 9 AM and 11 PM.");
                    }
                  }}
                />
              </div>
            </div>

            {/* Pickup Location */}
            {deliveryType === "pickup" && (
              <div className="mt-4">
                <h3 className="font-semibold text-lg text-pink-700 mb-2">
                  Pickup Location
                </h3>
                <p className="text-gray-600 mb-2">
                  You can pick up your order from our bakery at the following location:
                </p>
                <iframe
                  src={businessInfo.mapEmbedUrl}
                  width="100%"
                  height="250"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
                <p className="text-gray-500 mt-2">
                  {/* Visit us during our working hours to pick up your fresh bakery goodies! 🍰 */}
                </p>
              </div>
            )}

            {/* Delivery Address */}
            {deliveryType === "delivery" && (
              <div className="mt-4 space-y-2">
                <h3 className="font-semibold text-lg text-pink-700 mb-2">Delivery Address Details</h3>
                <input type="text" placeholder="Recipient Name" className="border w-full p-2 rounded" value={recipientName} onChange={(e) => setRecipientName(e.target.value)} />
                <input type="text" placeholder="Mobile Number" className="border w-full p-2 rounded" value={recipientPhone} onChange={(e) => setRecipientPhone(e.target.value)} />
                <input type="text" placeholder="House / Apartment Number" className="border w-full p-2 rounded" value={houseNumber} onChange={(e) => setHouseNumber(e.target.value)} />
                <input type="text" placeholder="Address" className="border w-full p-2 rounded" value={addressLine1} onChange={(e) => setAddressLine1(e.target.value)} />
                <input type="text" placeholder="Postal Code" className="border w-full p-2 rounded" value={postalCode} onChange={(e) => setPostalCode(e.target.value)} />
              </div>
            )}

            {/* Map */}
            {deliveryType === "delivery" && (
              <div className="mt-4">
                <h3 className="font-semibold text-lg text-pink-700 mb-2">Select Delivery Location on Map</h3>
                <div id="map" style={{ height: "300px", width: "100%" }}></div>
              </div>
            )}

            {!orderConfirmed ? (
              <button onClick={handleSendOtp} className="mt-4 w-full bg-gradient-to-r from-orange-500 to-pink-500 text-white py-3 rounded-full font-bold shadow-md hover:scale-105 transition-transform">
                Confirm Order 
                {/* //via OTP */}
              </button>
            ) : (
              <p className="text-green-600 font-semibold mt-4 text-center">✅ Order Confirmed Successfully!</p>
            )}
          </div>
        </div>
      </div>

      {/* OTP Modal */}
      {/* {showOtpModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 shadow-lg w-80 text-center">
            <h2 className="text-xl font-bold mb-3">Enter OTP</h2>
            <p className="text-gray-600 mb-4">Check your email for the 6-digit code</p>
            <input type="text" maxLength="6" className="border w-full p-2 rounded text-center text-lg tracking-widest focus:ring-2 focus:ring-pink-400 focus:outline-none" placeholder="Enter OTP" value={enteredOtp} onChange={(e) => setEnteredOtp(e.target.value)} />
            <div className="flex gap-4 mt-4">
              <button onClick={handleVerifyOtp} className="flex-1 bg-pink-600 text-white py-2 rounded hover:bg-pink-700">Verify</button>
              <button onClick={() => setShowOtpModal(false)} className="flex-1 border border-gray-400 py-2 rounded hover:bg-gray-100">Cancel</button>
            </div>
          </div>
        </div>
      )} */}
    </div>
  );
};

export default Cart;
